/**
 * Linked Project Service
 *
 * Real machinery behind "Link BIM" (.yap/.json native documents) and
 * "Load as Group": parse a foreign project payload, measure its real
 * extents, and instantiate its elements into the host document with
 * collision-free ids. Every instantiated element is tagged with the owning
 * LinkInstance id so Manage Links (hide / unload / remove) drives the whole
 * linked instance, not just the wrapper footprint.
 *
 * Binary Revit formats (.rvt/.rfa) are intentionally NOT handled here —
 * they cannot be decoded in this build and the link tools reject them with
 * an explicit "export to IFC" message instead of drawing a fake box.
 */

/** Minimal shape of a foreign element we can instantiate. */
interface ForeignElement {
    id: string;
    category: string;
    type?: string;
    parameters?: Record<string, any>;
    constraints?: { type: string; targetId?: string; data?: any }[];
    geometryData?: any;
    [key: string]: any;
}

export interface LinkedProjectPayload {
    /** [id, element] entry pairs in kernel serialization order. */
    elementEntries: [string, ForeignElement][];
    levels: [string, any][];
}

export interface BBox {
    min: [number, number, number];
    max: [number, number, number];
}

/**
 * Parse a .yap/.json document payload (envelope or legacy raw kernel JSON)
 * down to its element + level entries. Returns null for anything that is
 * not a Yap project document — callers surface a clear "unsupported file"
 * error, they never fabricate content.
 */
export function parseLinkedProjectText(text: string): LinkedProjectPayload | null {
    let parsed: any;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;

    let project: any = null;
    if (parsed.format === 'yap-project' || parsed.format === 'yap-template') {
        project = parsed.project;
    } else if (Array.isArray(parsed.elements)) {
        // Legacy pre-envelope saves are the raw serialized kernel project.
        project = parsed;
    }
    if (!project || !Array.isArray(project.elements)) return null;

    const elementEntries = (project.elements as any[]).filter(
        ([, el]) => el && typeof el === 'object' && typeof el.category === 'string'
    ) as [string, ForeignElement][];
    if (elementEntries.length === 0) return null;

    const levels = Array.isArray(project.levels)
        ? (project.levels as any[]).filter(([, lvl]) => lvl && typeof lvl === 'object')
        : [];
    return { elementEntries, levels };
}

/** Point-like numeric triples worth feeding the extents measurement. */
const POINT_KEYS = new Set(['start', 'end', 'location', 'position', 'origin', 'center', 'midPoint']);
/** Polygon keys carrying [x, y] pairs (z supplied by the companion offset field). */
const PAIR_KEYS = new Set(['boundary']);

/**
 * Measure the real extents of foreign elements from their geometry data.
 * Walks the known point/polygon fields of the element schema (walls
 * start/end, floors boundary, columns location, MEP start/end, component
 * position) plus height/offset scalars for the z range. Hosted elements
 * (doors/windows) carry no own geometry — the host wall bounds cover them.
 */
export function computeElementsBBox(entries: [string, ForeignElement][]): BBox {
    const min: [number, number, number] = [Infinity, Infinity, Infinity];
    const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
    let found = false;

    const grow = (x: number, y: number, z: number) => {
        if (!isFinite(x) || !isFinite(y) || !isFinite(z)) return;
        found = true;
        min[0] = Math.min(min[0], x); max[0] = Math.max(max[0], x);
        min[1] = Math.min(min[1], y); max[1] = Math.max(max[1], y);
        min[2] = Math.min(min[2], z); max[2] = Math.max(max[2], z);
    };

    for (const [, el] of entries) {
        const g = el.geometryData;
        if (!g || typeof g !== 'object') continue;

        // Explicit triples (walls, MEP, columns, components).
        for (const key of POINT_KEYS) {
            const p = g[key];
            if (Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number') {
                grow(p[0], p[1], typeof p[2] === 'number' ? p[2] : 0);
            }
        }
        // Slab/roof boundaries: [x, y] pairs lifted by the level offset.
        for (const key of PAIR_KEYS) {
            const poly = g[key];
            if (!Array.isArray(poly)) continue;
            const z = Number(g.heightOffsetFromLevel ?? g.baseOffset ?? 0) || 0;
            for (const p of poly) {
                if (Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number') {
                    grow(p[0], p[1], z);
                }
            }
        }
        // Vertical scalars extend the z range around every grown point.
        const zGrow = [g.height, g.thickness, g.topOffset, g.baseOffset]
            .filter((v): v is number => typeof v === 'number' && isFinite(v));
        if (zGrow.length > 0 && found) {
            grow(min[0], min[1], min[2] - Math.max(...zGrow.map(Math.abs)));
        }
    }

    if (!found) return { min: [0, 0, 0], max: [0, 0, 0] };
    return { min, max };
}

export function bboxSize(bbox: BBox): [number, number, number] {
    return [
        Math.max(bbox.max[0] - bbox.min[0], 0.5),
        Math.max(bbox.max[1] - bbox.min[1], 0.5),
        Math.max(bbox.max[2] - bbox.min[2], 0.5),
    ];
}

export interface InstantiatedLinkedElements {
    /** Cloned + remapped host-side elements, tagged with the link instance id. */
    elements: ForeignElement[];
    idMap: Map<string, string>;
    bbox: BBox;
}

/**
 * Deep-clone foreign elements into host-document elements: ids are
 * remapped to unique host ids (constraints and host references follow via
 * the same map), and each clone is tagged with `linkInstanceId` so Manage
 * Links drives every member. The source payload is never mutated.
 */
export function instantiateLinkedElements(
    entries: [string, ForeignElement][],
    linkInstanceId: string,
    options: { offset?: [number, number, number] } = {}
): InstantiatedLinkedElements {
    const idMap = new Map<string, string>();
    for (const [foreignId] of entries) {
        idMap.set(foreignId, `${foreignId}::${linkInstanceId.slice(0, 8)}`);
    }
    const offset = options.offset ?? [0, 0, 0];

    const elements = entries.map(([foreignId, el]) => {
        const clone: ForeignElement = JSON.parse(JSON.stringify(el));
        clone.id = idMap.get(foreignId)!;
        clone.linkInstanceId = linkInstanceId;

        // Constraints reference host ids (hosts, levels) — remap what we know.
        if (Array.isArray(clone.constraints)) {
            clone.constraints = clone.constraints.map((c) => ({
                ...c,
                targetId: c.targetId && idMap.has(c.targetId) ? idMap.get(c.targetId)! : c.targetId,
            }));
        }
        if (clone.geometryData && typeof clone.geometryData === 'object') {
            // Hosted openings point at their wall by id, not via constraints.
            if (typeof clone.geometryData.hostWallId === 'string' && idMap.has(clone.geometryData.hostWallId)) {
                clone.geometryData.hostWallId = idMap.get(clone.geometryData.hostWallId)!;
            }
            if (offset.some((o) => o !== 0)) {
                translateGeometry(clone.geometryData, offset);
            }
        }
        return clone;
    });

    return { elements, idMap, bbox: computeElementsBBox(entries) };
}

/**
 * Shift the measured point fields of a cloned geometry by a placement
 * offset. Mirrors the point keys the extents walker understands so the
 * instantiated model lands exactly where the user clicked.
 */
function translateGeometry(g: Record<string, any>, offset: [number, number, number]): void {
    const shift = (p: any[]) => {
        p[0] += offset[0];
        if (p.length > 1) p[1] += offset[1];
        if (p.length > 2) p[2] += offset[2];
    };
    for (const key of POINT_KEYS) {
        const p = g[key];
        if (Array.isArray(p) && typeof p[0] === 'number') shift(p);
    }
    for (const key of PAIR_KEYS) {
        const poly = g[key];
        if (!Array.isArray(poly)) continue;
        for (const p of poly) {
            if (Array.isArray(p) && typeof p[0] === 'number') shift(p);
        }
    }
}

/**
 * Collect every host element that belongs to a linked instance (members +
 * wrapper). Manage Links hide/unload/remove operates on this set.
 */
export function getLinkedInstanceElementIds(elements: Map<string, any>, linkInstanceId: string): string[] {
    const ids: string[] = [];
    for (const [id, el] of elements) {
        if ((el as any)?.linkInstanceId === linkInstanceId) ids.push(id);
    }
    return ids;
}
